import { CreativeDocumentContent } from 'content-creation-common/models';

import Medium from './Medium';
import { deepEqual, isEmptyString, isNil } from '../util/object';
import {
  CONTENT_TYPE,
  DEFAULT_FILL_COLOR,
  DEFAULT_LINE_SPACING,
  DEFAULT_TEXT_EFFECTS_OBJECTS,
  MAX_MEDIA_DURATION_SECONDS,
  SHAPE,
  TEXT_ALIGNMENT,
} from '../constants';
import {
  DEFAULT_FONT_PROPERTIES,
  DEFAULT_TEXT_FILL,
  DEFAULT_TEXT_TYPE,
  DEFAULT_TEXT_WRAP_BOX_HEIGHT_IN_PIXELS,
  DEFAULT_TEXT_WRAP_BOX_WIDTH_IN_PIXELS,
  DEFAULT_VERTICAL_TEXT_ALIGNMENT,
  OVERFLOW_X,
  OVERFLOW_Y,
} from '../features/text/constants';
import { getPlaceholderText } from '../util/messages';

export default class Content {
  /*
    Member variables, please set defaults in constructor (for now)
   */
  _audible;
  _contentElementIds;
  _contentId;
  _contentType;
  _creativeFieldId;
  _endTime;
  _fillColor;
  _font;
  _fontSize;
  _isOpen;
  _letterSpacing;
  _lineSpacing;
  _medium;
  _naturalHeight;
  _naturalWidth;
  _overflowX;
  _overflowY;
  _padding;
  _renderedFontSize;
  _shape;
  _startTime;
  _strokeColor;
  _strokeWidth;
  _textAlignment;
  _textEffect;
  _textFill;
  _textLines;
  _textString;
  _textType;
  _textVerticalAlignment;
  _textWrap;
  _visible;

  /**
   * @param {String} contentType
   * @param {String|null} contentId
   * @param {Medium|null} medium
   */
  // TODO group text properties in to text object model for convenience?
  constructor(contentType, contentId = null, medium = null) {
    if (
      isEmptyString(contentType) ||
      isNil(CONTENT_TYPE[contentType.toUpperCase()])
    ) {
      throw new Error('Content requires valid contentType');
    }

    this._contentType = contentType.toUpperCase();
    this._contentId = isEmptyString(contentId)
      ? CreativeDocumentContent.getId()
      : contentId;
    this._medium = medium;

    // Per-type defaults
    // TODO(lemieux) refactor? Subclasses?
    if (contentType === CONTENT_TYPE.TEXT) {
      this._audible = false;
      this._fillColor = DEFAULT_FONT_PROPERTIES.color;
      this._fontSize = `${DEFAULT_FONT_PROPERTIES.fontSize}px`;
      this._letterSpacing = 0;
      this._lineSpacing = DEFAULT_LINE_SPACING;
      this._overflowX = OVERFLOW_X.WRAP;
      this._overflowY = OVERFLOW_Y.VISIBLE;
      this._padding = 0;
      this._renderedFontSize = this._fontSize;
      this._strokeColor = DEFAULT_FONT_PROPERTIES.strokeColor;
      this._strokeWidth = DEFAULT_FONT_PROPERTIES.strokeWidth;
      this._textAlignment = TEXT_ALIGNMENT.LEFT;
      this._textEffect = {
        ...DEFAULT_TEXT_EFFECTS_OBJECTS[DEFAULT_FONT_PROPERTIES.textEffect],
      };
      this._textFill = {
        mode: DEFAULT_TEXT_FILL,
      };
      this._textString = getPlaceholderText();
      this._textVerticalAlignment = DEFAULT_VERTICAL_TEXT_ALIGNMENT;
      this._textType = DEFAULT_TEXT_TYPE;
      this._textWrap = {
        breakWord: false,
        height: DEFAULT_TEXT_WRAP_BOX_HEIGHT_IN_PIXELS,
        width: DEFAULT_TEXT_WRAP_BOX_WIDTH_IN_PIXELS,
      };
    }

    if (contentType === CONTENT_TYPE.SHAPE) {
      this._audible = false;
      this._fillColor = DEFAULT_FILL_COLOR;
      this._shape = SHAPE.SQUARE;
    }

    if (
      contentType === CONTENT_TYPE.ELEMENT_GROUP ||
      contentType === CONTENT_TYPE.ELEMENT_EFFECT_GROUP
    ) {
      this._contentElementIds = [];
    }

    if (contentType === CONTENT_TYPE.AUDIO) {
      this._visible = false;
    }

    if (contentType === CONTENT_TYPE.VIDEO) {
      this._startTime = 0;
      this._endTime = MAX_MEDIA_DURATION_SECONDS;
    }
  }

  /*
    Convenience Methods
   */

  equals(inContent) {
    if (!Content.isContent(inContent)) return false;

    const propertyEquality =
      this.audible === inContent.audible &&
      this.contentId === inContent.contentId &&
      this.contentType === inContent.contentType &&
      this.creativeFieldId === inContent.creativeFieldId &&
      this.endTime === inContent.endTime &&
      this.isOpen === inContent.isOpen &&
      this.fillColor === inContent.fillColor &&
      this.font === inContent.font &&
      this.fontSize === inContent.fontSize &&
      this.letterSpacing === inContent.letterSpacing &&
      this.lineSpacing === inContent.lineSpacing &&
      this.naturalHeight === inContent.naturalHeight &&
      this.naturalWidth === inContent.naturalWidth &&
      this.overflowX === inContent.overflowX &&
      this.overflowY === inContent.overflowY &&
      this.naturalWidth === inContent.naturalWidth &&
      this.padding === inContent.padding &&
      this.renderedFontSize === inContent.renderedFontSize &&
      this.shape === inContent.shape &&
      this.startTime === inContent.startTime &&
      this.strokeColor === inContent.strokeColor &&
      this.strokeWidth === inContent.strokeWidth &&
      this.textAlignment === inContent.textAlignment &&
      this.textString === inContent.textString &&
      this.textType === inContent.textType &&
      this.textVerticalAlignment === inContent.textVerticalAlignment &&
      this.visible === inContent.visible &&
      deepEqual(this.contentElementIds, inContent.contentElementIds) &&
      deepEqual(this.textEffect, inContent.textEffect) &&
      deepEqual(this.textFill, inContent.textFill) &&
      deepEqual(this.textLines, inContent.textLines) &&
      deepEqual(this.textWrap, inContent.textWrap);

    if (!propertyEquality) return false;

    if (Medium.isMedium(this.medium)) {
      const mediumEquality = this.medium.equals(inContent.medium);
      if (!mediumEquality) return false;
    } else if (Medium.isMedium(inContent.medium)) {
      return false;
    }

    return true;
  }

  /*
    Getters and Setters for content properties
   */

  get audible() {
    return this._audible;
  }

  set audible(value) {
    this._audible = value;
  }

  // TODO make this a list and have convenience methods for adding/removing
  get contentElementIds() {
    return this._contentElementIds;
  }

  set contentElementIds(value) {
    this._contentElementIds = value;
  }

  get contentId() {
    return this._contentId;
  }

  set contentId(value) {
    this._contentId = value;
  }

  get contentType() {
    return this._contentType;
  }

  set contentType(value) {
    this._contentType = value;
  }

  get creativeFieldId() {
    return this._creativeFieldId;
  }

  set creativeFieldId(value) {
    this._creativeFieldId = value;
  }

  get endTime() {
    return this._endTime;
  }

  set endTime(value) {
    this._endTime = value;
  }

  get fillColor() {
    return this._fillColor;
  }

  set fillColor(value) {
    this._fillColor = value;
  }

  get font() {
    return this._font;
  }

  set font(value) {
    this._font = value;
  }

  get fontSize() {
    return this._fontSize;
  }

  set fontSize(value) {
    this._fontSize = value;
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    this._isOpen = value;
  }

  get letterSpacing() {
    return this._letterSpacing;
  }

  set letterSpacing(value) {
    this._letterSpacing = value;
  }

  get lineSpacing() {
    return this._lineSpacing;
  }

  set lineSpacing(value) {
    this._lineSpacing = value;
  }

  get medium() {
    return this._medium;
  }

  set medium(value) {
    this._medium = value;
  }

  get naturalHeight() {
    return this._naturalHeight;
  }

  set naturalHeight(value) {
    this._naturalHeight = value;
  }

  get naturalWidth() {
    return this._naturalWidth;
  }

  set naturalWidth(value) {
    this._naturalWidth = value;
  }

  get overflowX() {
    return this._overflowX;
  }

  set overflowX(value) {
    this._overflowX = value;
  }

  get overflowY() {
    return this._overflowY;
  }

  set overflowY(value) {
    this._overflowY = value;
  }

  get padding() {
    return this._padding;
  }

  set padding(value) {
    this._padding = value;
  }

  get renderedFontSize() {
    return this._renderedFontSize;
  }

  set renderedFontSize(value) {
    this._renderedFontSize = value;
  }

  get shape() {
    return this._shape;
  }

  set shape(value) {
    this._shape = value;
  }

  get startTime() {
    return this._startTime;
  }

  set startTime(value) {
    this._startTime = value;
  }

  get strokeColor() {
    return this._strokeColor;
  }

  set strokeColor(value) {
    this._strokeColor = value;
  }

  get strokeWidth() {
    return this._strokeWidth;
  }

  set strokeWidth(value) {
    this._strokeWidth = value;
  }

  get textAlignment() {
    return this._textAlignment;
  }

  set textAlignment(value) {
    this._textAlignment = value;
  }

  get textEffect() {
    return this._textEffect;
  }

  set textEffect(value) {
    this._textEffect = value;
  }

  get textFill() {
    return this._textFill;
  }

  set textFill(value) {
    this._textFill = value;
  }

  get textLines() {
    return this._textLines;
  }

  set textLines(value) {
    this._textLines = value;
  }

  get textString() {
    return this._textString;
  }

  set textString(value) {
    this._textString = value;
  }

  get textType() {
    return this._textType;
  }

  set textType(value) {
    this._textType = value;
  }

  get textVerticalAlignment() {
    return this._textVerticalAlignment;
  }

  set textVerticalAlignment(value) {
    this._textVerticalAlignment = value;
  }

  get textWrap() {
    return this._textWrap;
  }

  set textWrap(value) {
    this._textWrap = value;
  }

  get visible() {
    return this._visible;
  }

  set visible(value) {
    this._visible = value;
  }

  /*
    Convenience getters
   */

  get hasMedium() {
    return Medium.isMedium(this.medium);
  }

  get mediumId() {
    return this.hasMedium ? this.medium.mediaId : undefined;
  }

  /*
    Static Methods
   */

  /**
   * @param {Object} inContent
   * @returns {boolean}
   */
  static isContent(inContent) {
    return inContent instanceof Content;
  }

  /**
   * @param {CreativeDocumentContent} creativeDocumentContent
   * @param {CreativeDocumentMedium|null}creativeDocumentMedium
   * @returns {Content}
   */
  static fromCreativeDocumentObject(
    creativeDocumentContent,
    creativeDocumentMedium,
  ) {
    const medium = Medium.fromCreativeDocumentObject(creativeDocumentMedium);

    const {
      alignment,
      audible,
      contentElementIds,
      contentId,
      contentType,
      creativeFieldId,
      end,
      fillColor,
      font,
      fontSize,
      isOpen,
      letterSpacing,
      lineSpacing,
      naturalHeight,
      naturalWidth,
      overflowX,
      overflowY,
      padding,
      renderedFontSize,
      shape,
      start,
      strokeColor,
      strokeWidth,
      textEffect,
      textFill,
      textLines,
      textString,
      textType,
      textWrap,
      verticalAlignment,
      visible,
    } = creativeDocumentContent;

    const content = new Content(contentType, contentId, medium);
    content.audible = audible;
    content.contentElementIds = contentElementIds;
    content.creativeFieldId = creativeFieldId;
    content.endTime = end;
    content.fillColor = fillColor;
    content.font = font;
    content.fontSize = fontSize;
    content.isOpen = isOpen;
    content.letterSpacing = letterSpacing;
    content.lineSpacing = lineSpacing;
    content.naturalHeight = naturalHeight;
    content.naturalWidth = naturalWidth;
    content.overflowX = overflowX;
    content.overflowY = overflowY;
    content.padding = padding;
    content.renderedFontSize = renderedFontSize;
    content.shape = shape;
    content.startTime = start;
    content.strokeColor = strokeColor;
    content.strokeWidth = strokeWidth;
    content.textAlignment = alignment;
    content.textEffect = textEffect;
    content.textFill = textFill;
    content.textLines = textLines;
    content.textString = textString;
    content.textType = textType;
    content.textVerticalAlignment = verticalAlignment;
    content.textWrap = textWrap;
    content.visible = visible;

    return content;
  }

  /**
   * @param {Content} content
   * @returns {CreativeDocumentContent}
   */
  static getCreativeDocumentContent(content) {
    if (!Content.isContent(content)) {
      throw new Error('Cannot get CreativeDocumentContent');
    }

    const creativeDocumentContent = new CreativeDocumentContent(
      content.contentType,
      content.contentId,
      content.mediumId,
    );

    // TODO - audit other properties
    creativeDocumentContent.alignment = content.textAlignment;
    creativeDocumentContent.audible = content.audible;
    creativeDocumentContent.contentElementIds = content.contentElementIds;
    creativeDocumentContent.creativeFieldId = content.creativeFieldId;
    creativeDocumentContent.end = content.endTime;
    creativeDocumentContent.fillColor = content.fillColor;
    creativeDocumentContent.font = content.font;
    creativeDocumentContent.fontSize = content.fontSize;
    creativeDocumentContent.isOpen = content.isOpen;
    creativeDocumentContent.letterSpacing = content.letterSpacing;
    creativeDocumentContent.lineSpacing = content.lineSpacing;
    creativeDocumentContent.naturalHeight = content.naturalHeight;
    creativeDocumentContent.naturalWidth = content.naturalWidth;
    creativeDocumentContent.overflowX = content.overflowX;
    creativeDocumentContent.overflowY = content.overflowY;
    creativeDocumentContent.padding = content.padding;
    creativeDocumentContent.renderedFontSize = content.renderedFontSize;
    creativeDocumentContent.shape = content.shape;
    creativeDocumentContent.start = content.startTime;
    creativeDocumentContent.strokeColor = content.strokeColor;
    creativeDocumentContent.strokeWidth = content.strokeWidth;
    creativeDocumentContent.textEffect = content.textEffect;
    creativeDocumentContent.textFill = content.textFill;
    creativeDocumentContent.textLines = content.textLines;
    creativeDocumentContent.textString = content.textString;
    creativeDocumentContent.textType = content.textType;
    creativeDocumentContent.textWrap = content.textWrap;
    creativeDocumentContent.verticalAlignment = content.textVerticalAlignment;
    creativeDocumentContent.visible = content.visible;

    return creativeDocumentContent;
  }
}
